import { createSlice, createSelector } from "@reduxjs/toolkit";
import { apiCallBegan } from "./api";
import config from "../../config.json";
import { createRequestState, requestFailed, requestStarted, requestSucceeded } from "./requestState";

const slice = createSlice({
  name: "users",
  initialState: {
    list: [],
    loading: false,
    lastFetch: null,
    addedMessage: false,
    emailConfig: null,
    emailConfigUpdateStatus: false,
    activatedDeactivatedStatus: false,
    accessLogHistory: [],
    mailHistory: [],
    mailSendStatus: false,
    mailDeleteStatus: false,
    mailAddStatus: false,
    mailDocUploadSasRes: null,
    mailUpdateStatus: false,
    permissionUpdateStatus: null,
    requests: {
      rolePermissionUpdate: createRequestState(),
      activateDeactivate: createRequestState(),
    },
  },
  reducers: {
    userRequestStarted: (users, action) => {
      requestStarted(users.requests, action.payload);
    },
    userRequestFailed: (users, action) => {
      requestFailed(users.requests, action.payload?.key, action.payload?.error || action.payload);
    },
    usersReceived: (users, action) => {
      users.list = action.payload;
      users.addedMessage = false;
    },
    userAdded: (users, action) => {
      users.addedMessage = true;
    },
    filterUsersReceived: (users, action) => {
      users.filterList = action.payload;
    },
    emailConfigReceived: (users, action) => {
      users.emailConfig = action.payload;
      users.emailConfigUpdateStatus = false;
    },
    emailConfigUpdated: (users, action) => {
      users.emailConfigUpdateStatus = true;
    },
    userActiveDeactiveUpdated: (users, action) => {
      users.activatedDeactivatedStatus = true;
      requestSucceeded(users.requests, "activateDeactivate");
    },
    userAccessLogHistoryReceived: (users, action) => {
      users.accessLogHistory = action.payload;
      users.activatedDeactivatedStatus = false;
    },
    mailHistoryReceived: (users, action) => {
      users.mailHistory = action.payload;
      users.mailSendStatus = false;
      users.mailDeleteStatus = false;
      users.mailUpdateStatus = false;
      users.mailAddStatus = false;
      users.mailDocUploadSasRes = null;
    },
    emailMessageSent: (users, action) => {
      users.mailSendStatus = action.payload;
    },
    addedEmailMessageSent: (users, action) => {
      // successfully sent added mail, do not set mailSendStatus to prevent duplicate draft alert
    },
    emailMessageDeleted: (users, action) => {
      users.mailDeleteStatus = true;
    },
    emailMessageAdded: (users, action) => {
      const payload = action.payload;
      if (!payload) {
        users.mailAddStatus = null;
        users.mailDocUploadSasRes = null;
        return;
      }

      users.mailAddStatus = payload?.length > 0 ? payload.length : payload;
      users.mailDocUploadSasRes = payload ?? null;
    },
    emailMessageUpdated: (users, action) => {
      users.mailUpdateStatus = action.payload;
    },
    rolePermissionUpdated: (users, action) => {
      users.permissionUpdateStatus = action.payload;
      requestSucceeded(users.requests, "rolePermissionUpdate", action.payload);
    },
  },
});

export const {
  userRequestStarted,
  userRequestFailed,
  usersReceived,
  userAdded,
  filterUsersReceived,
  emailConfigReceived,
  emailConfigUpdated,
  userActiveDeactiveUpdated,
  userAccessLogHistoryReceived,
  mailHistoryReceived,
  emailMessageSent,
  emailMessageDeleted,
  emailMessageAdded,
  emailMessageUpdated,
  addedEmailMessageSent,
  rolePermissionUpdated,
} = slice.actions;
export default slice.reducer;

export const loadRegisteredUsers = (pageSize, pageIndex, type) =>
  apiCallBegan({
    url:
      config.GetRegisteredUsers +
      pageSize +
      "&pageNumber=" +
      pageIndex +
      "&type=" +
      type,
    onSuccess: usersReceived.type,
  });

export const loadEmailConfig = () =>
  apiCallBegan({
    url: config.GetEmailSetup,
    onSuccess: emailConfigReceived.type,
  });

export const loadAllEmailHistory = (id) =>
  apiCallBegan({
    url: config.GetAllEmails + id,
    onSuccess: mailHistoryReceived.type,
  });

export const SendEmailMessage = (id) =>
  apiCallBegan({
    url: config.SendMailMessage + id,
    onSuccess: emailMessageSent.type,
  });

export const SendAddedEmailMessage = (id) =>
  apiCallBegan({
    url: config.SendMailMessage + id,
    onSuccess: addedEmailMessageSent.type,
  });

export const DeleteEmailMessage = (id) =>
  apiCallBegan({
    url: config.DeleteMailMessage + id,
    onSuccess: emailMessageDeleted.type,
  });

export const updateEmailConfig = (data) =>
  apiCallBegan({
    url: config.SaveEmailSetup,
    method: "post",
    data: data,
    onSuccess: emailConfigUpdated.type,
  });

export const addNewEmailMessage = (data) =>
  apiCallBegan({
    url: config.AddNewEmail,
    method: "post",
    data: data,
    onSuccess: emailMessageAdded.type,
  });

export const updateEmailMessage = (data) =>
  apiCallBegan({
    url: config.UpdateMailMessage,
    method: "post",
    data: data,
    onSuccess: emailMessageUpdated.type,
  });

export const loadRegisteredUsersByFilter = (data) =>
  apiCallBegan({
    url: config.GetRegisteredUsersByFilter,
    method: "post",
    data: data,
    onSuccess: filterUsersReceived.type,
  });

export const loadUserAccessLogHistory = (id, type) =>
  apiCallBegan({
    url: config.GetAccessRightHistory + id + "&type=" + type,
    onSuccess: userAccessLogHistoryReceived.type,
  });

export const updateUserActiveDeactive = (data) =>
  apiCallBegan({
    url: config.SetActivateDeactivate,
    method: "post",
    data: data,
    onStart: { type: userRequestStarted.type, payload: "activateDeactivate" },
    onSuccess: userActiveDeactiveUpdated.type,
    onError: { type: userRequestFailed.type, payload: { key: "activateDeactivate" } },
  });

export const addEditPermission = (data) =>
  apiCallBegan({
    url: config.AddEditRolePermission,
    method: "post",
    data: data,
    onStart: { type: userRequestStarted.type, payload: "rolePermissionUpdate" },
    onSuccess: rolePermissionUpdated.type,
    onError: { type: userRequestFailed.type, payload: { key: "rolePermissionUpdate" } },
    throwOnError: true,
  });

export const addWorkflowPerformer = (data) =>
  apiCallBegan({
    url: config.SetupUsers.AddWorkflowPerformer,
    method: "post",
    data: data,
    onSuccess: userAdded.type,
    throwOnError: true,
  });


export const getRegisteredUsers = createSelector(
  (state) => state.entities.users,
  (users) => users.list,
);

export const getFilterAllUsers = createSelector(
  (state) => state.entities.users,
  (users) => users.filterList,
);

export const getUserAccessHistory = createSelector(
  (state) => state.entities.users,
  (users) => users.accessLogHistory,
);

export const getActivateDeactivateUpdateStatus = createSelector(
  (state) => state.entities.users,
  (users) => users.activatedDeactivatedStatus,
);

export const getEmailConfig = createSelector(
  (state) => state.entities.users,
  (users) => users.emailConfig,
);

export const getEmailConfigUpdateStatus = createSelector(
  (state) => state.entities.users,
  (users) => users.emailConfigUpdateStatus,
);

export const getAllEmailHistory = createSelector(
  (state) => state.entities.users,
  (users) => users.mailHistory,
);

export const getSentEmailStatus = createSelector(
  (state) => state.entities.users,
  (users) => users.mailSendStatus,
);

export const getDeleteEmailStatus = createSelector(
  (state) => state.entities.users,
  (users) => users.mailDeleteStatus,
);

export const getEmailAddedStatus = createSelector(
  (state) => state.entities.users,
  (users) => users.mailAddStatus,
);

export const getMailDocUploadSasRes = createSelector(
  (state) => state.entities.users,
  (users) => users.mailDocUploadSasRes,
);

export const getEmailUpdatedStatus = createSelector(
  (state) => state.entities.users,
  (users) => users.mailUpdateStatus,
);

export const getPermissionUpdateStatus = createSelector(
  (state) => state.entities.users,
  (users) => users.permissionUpdateStatus,
);

export const getUserRequests = createSelector(
  (state) => state.entities.users,
  (users) => users.requests,
);

export const getActivateDeactivateLoading = createSelector(
  (state) => state.entities.users,
  (users) => users.requests.activateDeactivate?.loading || false,
);
